import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ArrowUp, ArrowDown, Equal } from 'lucide-react';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useBulkStocktakeUpload } from '../useBulkStocktakeUpload';
import { AdjustmentReferenceField } from '../../AdjustmentReferenceField';

interface PreviewStepProps {
  onSubmit: () => void;
}

const PreviewStep: React.FC<PreviewStepProps> = ({ onSubmit }) => {
  const { t } = useTranslation('inventory');
  const { previewRows, notCountedRows, reference, setReference, setStep, setValidationErrors } = useBulkStocktakeUpload();
  const { format } = useCurrencyFormatter();

  const summary = useMemo(() => {
    let netChange = 0;
    let increasing = 0;
    let decreasing = 0;
    let unchanged = 0;

    let totalCost = 0;
    let hasCostData = false;

    for (const row of previewRows) {
      netChange += row.difference;
      if (row.difference > 0) increasing++;
      else if (row.difference < 0) decreasing++;
      else unchanged++;
      if (row.costImpact != null) {
        totalCost += row.costImpact;
        hasCostData = true;
      }
    }

    return {
      skuCount: previewRows.length,
      netChange,
      increasing,
      decreasing,
      unchanged,
      totalCost,
      hasCostData,
    };
  }, [previewRows]);

  const handleChangeFile = () => {
    setValidationErrors([]);
    setStep('upload');
  };

  const formatChange = (diff: number) => {
    if (diff > 0) return `+${diff.toLocaleString()}`;
    if (diff < 0) return diff.toLocaleString();
    return null;
  };

  const formatNetChange = (net: number) => {
    if (net > 0) return `+${net.toLocaleString()}`;
    if (net < 0) return net.toLocaleString();
    return '0';
  };

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="flex items-center gap-2 text-sm px-1 flex-wrap">
        <span>{t('bulkStocktake.toUpdateLabel', { count: summary.skuCount })}</span>
        <span className="text-muted-foreground">|</span>
        <span>
          Net change: <strong>{formatNetChange(summary.netChange)}</strong> units
        </span>
        {summary.hasCostData && (
          <>
            <span className="text-muted-foreground">|</span>
            <span>
              Est. value impact:{' '}
              <strong className={
                summary.totalCost > 0
                  ? 'text-green-600 dark:text-green-400'
                  : summary.totalCost < 0
                    ? 'text-red-700 dark:text-red-400'
                    : 'text-muted-foreground'
              }>
                {summary.totalCost > 0 ? '+' : ''}{format(summary.totalCost)}
              </strong>
            </span>
          </>
        )}
      </div>

      {/* Not-counted rows: informational, never styled as an error — these
          rows are excluded from submission because they were left blank,
          not because anything is wrong with them. */}
      {notCountedRows.length > 0 && (
        <p className="text-xs text-muted-foreground px-1">
          {t('bulkStocktake.notCountedDesc', { count: notCountedRows.length })}
        </p>
      )}

      {/* Mini stats row */}
      <div className="flex items-center gap-4 text-xs px-1">
        <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
          <ArrowUp className="h-3.5 w-3.5" />
          {summary.increasing} increasing
        </span>
        <span className="flex items-center gap-1 text-red-700 dark:text-red-400">
          <ArrowDown className="h-3.5 w-3.5" />
          {summary.decreasing} decreasing
        </span>
        <span className="flex items-center gap-1 text-muted-foreground">
          <Equal className="h-3.5 w-3.5" />
          {summary.unchanged} unchanged
        </span>
      </div>

      {/* Preview table */}
      <div className="border rounded-md max-h-[340px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                SKU
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                Current &rarr; New
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                Change
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground hidden sm:table-cell">
                Unit Cost
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground hidden sm:table-cell">
                Cost Impact
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {previewRows.map((row) => {
              const diffColor =
                row.difference > 0
                  ? 'text-green-600 dark:text-green-400'
                  : row.difference < 0
                    ? 'text-red-700 dark:text-red-400'
                    : 'text-muted-foreground';

              return (
                // Keyed by (target org, sku): at a shared 3PL location the
                // owner and each client can stock the SAME sku string, so a
                // multi-client count sheet legitimately renders two rows whose
                // `sku` is identical. Keying on `sku` alone collided and React
                // warned on every render.
                <tr key={`${row.targetOrganizationId}::${row.sku}`} className="hover:bg-muted/50">
                  <td className="px-3 py-1.5 font-mono text-xs">
                    {row.sku}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-xs">
                    <span className="text-muted-foreground">{row.currentStock}</span>
                    <span className="text-muted-foreground mx-1">&rarr;</span>
                    <span className={`font-medium ${diffColor}`}>{row.newStock}</span>
                  </td>
                  <td className={`px-3 py-1.5 text-right tabular-nums text-xs font-medium ${diffColor}`}>
                    {row.difference !== 0 ? formatChange(row.difference) : (
                      <span>-</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-xs hidden sm:table-cell text-muted-foreground">
                    {row.unitCost != null ? format(row.unitCost) : '--'}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-xs hidden sm:table-cell">
                    {row.costImpact != null ? (
                      <span className={diffColor}>
                        {row.costImpact > 0 && '+'}
                        {format(row.costImpact)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">--</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Operator reference / order number — stored on the adjustment-
          document header so the whole stocktake reconciles as one doc. */}
      <div className="sm:max-w-xs">
        <AdjustmentReferenceField
          id="bulk-stocktake-reference"
          value={reference}
          onChange={setReference}
          labelClassName="text-xs"
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={handleChangeFile}>
          Change File
        </Button>
        <Button
          onClick={onSubmit}
          className="bg-emerald-600 text-white hover:bg-emerald-700"
        >
          Apply Stocktake to {previewRows.length} Item{previewRows.length !== 1 ? 's' : ''}
        </Button>
      </div>
    </div>
  );
};

export default PreviewStep;
